import React, { useEffect, useCallback, useState } from 'react';

/**
 * Modal component with unsaved-changes protection.
 *
 * Props:
 *   title        – modal title
 *   isOpen       – visibility flag
 *   onClose      – callback to close the modal
 *   children     – modal body
 *   size         – 'sm' | 'md' | 'lg'
 *   dirty        – if true, closing (overlay click, Escape, X button) shows a
 *                  confirmation prompt instead of closing immediately
 */
export default function Modal({ title, isOpen, onClose, children, size = 'md', dirty = false }) {
  const [showConfirm, setShowConfirm] = useState(false);

  // Reset confirm dialog when modal is opened/closed
  useEffect(() => {
    if (!isOpen) setShowConfirm(false);
  }, [isOpen]);

  // Attempt to close: if dirty → show confirmation, otherwise close
  const attemptClose = useCallback(() => {
    if (dirty) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [dirty, onClose]);

  // Escape key handling
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      if (showConfirm) {
        // If confirm dialog is visible, Escape cancels the confirmation (stays on modal)
        setShowConfirm(false);
      } else {
        attemptClose();
      }
    }
  }, [isOpen, showConfirm, attemptClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Overlay click → attempt close (not instant close)
  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) {
      attemptClose();
    }
  }

  // Confirmed discard
  function handleDiscard() {
    setShowConfirm(false);
    onClose();
  }

  // Cancel discard (stay on modal)
  function handleStay() {
    setShowConfirm(false);
  }

  const sizeClass = size === 'sm' ? 'modal-sm' : size === 'lg' ? 'modal-lg' : 'modal-md';

  return (
    <div
      className={`modal-overlay${isOpen ? ' open' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className={`modal ${sizeClass}`}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={attemptClose} aria-label="Close">
            {'\u2715'}
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>

        {/* Unsaved-changes confirmation overlay */}
        {showConfirm && (
          <div className="modal-confirm-overlay" onClick={e => e.stopPropagation()}>
            <div className="modal-confirm-dialog">
              <div className="modal-confirm-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <h3>Unsaved Changes</h3>
              <p>You have unsaved changes. Do you want to discard them?</p>
              <div className="modal-confirm-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleStay}>
                  Keep Editing
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleDiscard}>
                  Discard Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
